// src/components/Header.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import '../styles/Header.css';

export default function Header({ stats }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { sessionData, clearCache } = useSessionStorage();

  // Handle logout with proper cleanup
  const handleLogout = async () => {
    try {
      // Clear session storage first to prevent stale data issues
      clearCache();
      // Navigate to login immediately
      navigate('/login', { replace: true });
      // Then sign out from Firebase
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if logout fails
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      <header className="header">
        <div className="header__welcome">
          {sessionData.isLoadingProfile ? (
            <div className="header__welcome-skeleton"></div>
          ) : (
            <div className="header__welcome-content">
              <h1>
                {sessionData.userProfile?.displayName ? `Welcome back, ${sessionData.userProfile.displayName}` : 'Welcome'}
              </h1>
            </div>
          )}
          <div className="header__stats">
            {stats.map(({ label, value, loading }, index) => {
              let className = 'header__stat';
              
              // Add conditional styling based on label and value
              if (label.includes('Action') && value > 0) {
                className += ' header__stat--actions-needed';
              } else if (label.includes('Properties Completed') && value > 0) {
                className += ' header__stat--properties-completed';
              }
              
              // Add loaded class for animation
              if (!loading) {
                className += ' header__stat--loaded';
              }
              
              return (
                <div 
                  key={label} 
                  className={className}
                  style={{ 
                    '--animation-delay': `${index * 0.1}s`,
                    '--stagger-delay': `${index * 0.15}s`
                  }}
                >
                  {loading ? (
                    <>
                      <div 
                        className="stat__skeleton stat__skeleton--value"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      ></div>
                      <div 
                        className="stat__skeleton stat__skeleton--label"
                        style={{ animationDelay: `${index * 0.1 + 0.1}s` }}
                      ></div>
                    </>
                  ) : (
                    <div className="stat__content">
                      {/* Zero is a real answer: an advisor who has finished
                          their campaigns is shown 0, never a dash that reads
                          as "unknown". */}
                      <div className="stat__value">
                        {value === null || value === undefined || !Number.isFinite(Number(value)) ? 0 : value}
                      </div>
                      <div className="stat__label">{label}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="header__profile header__profile--with-nav">
          <div className="absolute-nav">
            {sessionData.userProfile?.profilePic && (
              <img
                src={sessionData.userProfile.profilePic}
                alt="Profile"
                className={`header__avatar header__avatar--${sessionData.userProfile?.profilePicShape || 'circle'}`}
              />
            )}
            <button className="header__logout" onClick={handleLogout} title="Sign Out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}